import React, { useEffect, useRef, useState } from "react";
import { Menu, MenuButton, MenuItems, Transition } from "@headlessui/react";
import { usePopper } from "react-popper";
import { bottom, left, right, top } from "@popperjs/core";
import Checkbox from "../../form/Checkbox";
import Radio from "../../form/Radio";
import { classNames } from "../../../helpers/classNames";
import Search from "../../form/Search";

const TableFilter = ({
  xPlacement = "bottomLeft",
  flip = true,
  filterData = [],
  isFilterSearch = true,
  onFilter = () => {},
  onScroll = () => {},
  filterSearch = () => {},
  ...props
}) => {
  const popperElRef = useRef(null);
  const buttonRef = useRef(null);
  const [targetElement, setTargetElement] = useState(null);
  const [popperElement, setPopperElement] = useState(null);
  const [selected, setSelected] = useState([]);
  const [keyword, setKeyword] = useState("");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!keyword) {
      filterSearch("");
    }
  }, [keyword]);

  const placements = {
    bottom: bottom,
    bottomLeft: "bottom-start",
    bottomRight: "bottom-end",
    top: top,
    topLeft: "top-start",
    topRight: "top-end",
    right: right,
    left: left,
    "": bottom,
  };

  const { styles, attributes } = usePopper(targetElement, popperElement, {
    placement: placements[xPlacement || "bottomLeft"],
    strategy: "fixed",
    modifiers: [
      {
        name: "offset",
        options: {
          offset: [0, 0],
        },
      },
      !flip && {
        name: "flip",
        options: {
          flipVariations: false,
          fallbackPlacements: ["bottom-end"],
        },
      },
    ],
  });

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        popperElRef.current &&
        !popperElRef.current.contains(event.target) &&
        !buttonRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <>
      <Menu as="div">
        {({ open }) => (
          <>
            <div className="relative group">
              <MenuButton
                ref={(el) => {
                  buttonRef.current = el;
                  setTargetElement(el);
                }}
                className="flex items-center"
                onClick={() => setOpen(!open)}
              >
                <div
                  className={classNames(
                    "relative flex items-center justify-center text-xs w-5 h-5 rounded text-deep-purple hover:shadow-lg transition-all bg-transparent duration-300 ease-in-out transform !ring-0 ",
                    selected.length > 0
                      ? "outline-1 outline outline-primary"
                      : ""
                  )}
                >
                  <i className="fa-solid fa-fw fa-filter-list"></i>
                </div>
              </MenuButton>
            </div>
            <Transition
              show={open}
              leave="transition duration-100 ease-out"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
              className="z-20"
              beforeEnter={() => setPopperElement(popperElRef.current)}
              afterLeave={() => setPopperElement(null)}
              ref={popperElRef}
              style={styles.popper}
              {...attributes.popper}
            >
              <MenuItems
                modal={false}
                ref={popperElRef}
                className="w-64 origin-top-right text bg-white border border-gray-200 divide-y divide-gray-100 rounded-lg shadow-lg ring-0"
              >
                {isFilterSearch && (
                  <div className="py-2 px-2 flex items-center justify-center gap-3 rounded-t-lg">
                    <Search
                      placeholder={"Search here."}
                      searchValue={keyword}
                      search={(e) => {
                        setKeyword(e);
                        filterSearch(e);
                      }}
                    />
                  </div>
                )}
                <div
                  onScroll={onScroll}
                  className="py-2 max-h-60 divide-y text-black divide-gray-100 overflow-auto scroll-smooth scrollbar"
                >
                  <div
                    onClick={() => {
                      const isAllSelected =
                        selected?.length === filterData?.length;
                      const updatedSelection = isAllSelected ? [] : filterData;
                      setSelected(updatedSelection);
                      onFilter(updatedSelection);
                    }}
                    className="cursor-pointer hover:bg-primary/10 transition-all duration-200"
                  >
                    <div className="relative py-2 px-4 -z-[1]">
                      <Checkbox
                        checkboxName={"filter_checkbox"}
                        checkboxInputClass={"!h-5 !w-5"}
                        checkboxClass={"!h-5 !w-5"}
                        checked={selected?.length === filterData?.length}
                        checkboxLabel={"Select All"}
                        checkboxLableClass={"font-medium text-sm"}
                        divClass={"w-full"}
                      />
                    </div>
                  </div>
                  {filterData.map((item, index) => (
                    <div
                      key={index?.toString()}
                      onClick={() => {
                        const isSelected = selected
                          ?.map((selectedItem) => selectedItem?.value)
                          ?.includes(item?.value);
                        const updatedSelection = isSelected
                          ? selected.filter(
                              (s_item) => s_item?.value !== item?.value
                            )
                          : [...selected, item];
                        setSelected(updatedSelection);
                        onFilter(updatedSelection);
                      }}
                      className="cursor-pointer text hover:bg-primary/10 transition-all duration-200"
                    >
                      {item.type === "checkbox" && (
                        <div className="relative py-2 px-4 -z-[1]">
                          <Checkbox
                            checkboxName={"filter_checkbox"}
                            checkboxInputClass={"!h-5 !w-5"}
                            checkboxClass={"!h-5 !w-5"}
                            checked={selected?.some(
                              (data) => data?.value === item?.value
                            )}
                            checkboxLabel={
                              item.name?.charAt(0).toUpperCase() +
                              item.name?.slice(1)
                            }
                            divClass={"w-full"}
                            checkboxLableClass={"font-normal"}
                          />
                        </div>
                      )}
                      {item.type === "radio" && (
                        <div className="relative py-2 px-4">
                          <Radio
                            radioName={"filter_radio"}
                            radioInputclassName={""}
                            isChecked={item.isChecked}
                            radioLabel={
                              item.name?.charAt(0).toUpperCase() +
                              item.name?.slice(1)
                            }
                            radioLableClass={"font-normal"}
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </MenuItems>
            </Transition>
          </>
        )}
      </Menu>
    </>
  );
};

export default TableFilter;
